const sectionSelector = '[data-cpsbrq]';

const normalizeToken = (value) =>
  String(value || '')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/[–—]/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9"+]+/g, ' ')
    .trim();

const splitTokens = (value) =>
  String(value || '')
    .split(/[,|\n]+/)
    .map((token) => normalizeToken(token))
    .filter(Boolean);

const tokenMatches = (cardToken, selectedToken) =>
  cardToken === selectedToken || cardToken.includes(selectedToken) || selectedToken.includes(cardToken);

const getProducts = (section) => {
  const script = section.querySelector('[data-cpsbrq-products]');

  if (!script) return [];

  try {
    const products = JSON.parse(script.textContent || '[]');
    return Array.isArray(products) ? products : [];
  } catch (error) {
    console.warn('Unable to parse shop-by-room quiz products.', error);
    return [];
  }
};

const getTokens = (product, key) => splitTokens(product?.[key]);

const hasToken = (product, key, value) => {
  const selectedToken = normalizeToken(value);
  if (!selectedToken) return false;

  return getTokens(product, key).some((token) => tokenMatches(token, selectedToken));
};

const productMatchesControl = (product, control) => {
  const tokens = getTokens(product, 'filterControl');

  if (control === 'smart') {
    return tokens.some(
      (token) =>
        tokenMatches(token, 'app') ||
        tokenMatches(token, 'voice ready') ||
        tokenMatches(token, 'voice-ready') ||
        tokenMatches(token, 'wifi') ||
        tokenMatches(token, 'wi fi')
    );
  }

  return tokens.some((token) => tokenMatches(token, 'remote'));
};

const getPreferredSize = (answers) => {
  const room = normalizeToken(answers.room);

  if (answers.control === 'smart') return '20"';
  if (room === 'kids room' || room === 'apartment') return '20"';
  if (room === 'bedroom' || room === 'home office' || room === 'dining room' || room === 'small living') return '42"';

  return '';
};

const scoreProduct = (product, answers, index) => {
  const preferredSize = getPreferredSize(answers);
  let score = 0;

  score += hasToken(product, 'filterFinish', answers.finish) ? 500 : -500;
  score += productMatchesControl(product, answers.control) ? 300 : -300;
  score += hasToken(product, 'filterRoom', answers.room) ? 160 : 0;
  score += preferredSize && hasToken(product, 'filterSize', preferredSize) ? 90 : 0;
  score -= index;

  return score;
};

const recommendProduct = (products, answers) => {
  if (!products.length) return null;

  return products
    .map((product, index) => ({
      product,
      score: scoreProduct(product, answers, index),
    }))
    .sort((a, b) => b.score - a.score)[0]?.product || null;
};

const setPressedInPanel = (panel, activeButton) => {
  panel.querySelectorAll('[data-cpsbrq-choice]').forEach((button) => {
    button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
  });
};

const focusPanel = (panel) => {
  if (!(panel instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    try {
      panel.focus({ preventScroll: true });
    } catch (error) {
      panel.focus();
    }
  });
};

function initSection(section) {
  if (!(section instanceof HTMLElement) || section.dataset.cpsbrqReady === 'true') return;

  const stepPanels = Array.from(section.querySelectorAll('[data-cpsbrq-step-panel]'));
  const resultPanel = section.querySelector('[data-cpsbrq-result]');
  const resultTitle = section.querySelector('[data-cpsbrq-result-title]');
  const resultDescription = section.querySelector('[data-cpsbrq-result-description]');
  const viewProductButton = section.querySelector('[data-cpsbrq-view-product]');
  const reloadButton = section.querySelector('[data-cpsbrq-reload]');
  const products = getProducts(section);
  const fallbackResultTitle = resultTitle?.textContent || '';
  const fallbackResultDescription = resultDescription?.textContent || '';

  if (!stepPanels.length || !(resultPanel instanceof HTMLElement)) return;

  const answers = {};
  let currentRecommendation = null;
  let advanceTimer = 0;

  section.dataset.cpsbrqReady = 'true';

  const showStep = (stepIndex, shouldFocus = false) => {
    window.clearTimeout(advanceTimer);
    section.dataset.cpsbrqStep = String(stepIndex);
    resultPanel.hidden = true;

    stepPanels.forEach((panel, index) => {
      panel.hidden = index !== stepIndex;
    });

    if (shouldFocus) {
      focusPanel(stepPanels[stepIndex]);
    }
  };

  const showResult = () => {
    currentRecommendation = recommendProduct(products, answers);

    if (resultTitle) {
      resultTitle.textContent = currentRecommendation?.title || fallbackResultTitle;
    }

    if (resultDescription) {
      resultDescription.textContent = currentRecommendation?.description || fallbackResultDescription;
    }

    if (viewProductButton instanceof HTMLButtonElement) {
      const canOpen = Boolean(currentRecommendation?.quickPanelId || currentRecommendation?.productHandle);
      viewProductButton.disabled = !canOpen;
      viewProductButton.setAttribute('aria-disabled', canOpen ? 'false' : 'true');
    }

    stepPanels.forEach((panel) => {
      panel.hidden = true;
    });

    resultPanel.hidden = false;
    section.dataset.cpsbrqStep = 'result';
    focusPanel(resultPanel);
  };

  const handleChoice = (choice) => {
    const panel = choice.closest('[data-cpsbrq-step-panel]');
    const stepIndex = Number(panel?.dataset.cpsbrqStep || 0);
    const key = choice.dataset.cpsbrqKey;

    if (!(panel instanceof HTMLElement) || !key) return;

    answers[key] = choice.dataset.cpsbrqValue || '';
    setPressedInPanel(panel, choice);

    window.clearTimeout(advanceTimer);
    advanceTimer = window.setTimeout(() => {
      if (stepIndex < stepPanels.length - 1) {
        showStep(stepIndex + 1, true);
      } else {
        showResult();
      }
    }, 120);
  };

  const resetQuiz = () => {
    currentRecommendation = null;
    Object.keys(answers).forEach((key) => delete answers[key]);

    section.querySelectorAll('[data-cpsbrq-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
    });

    if (viewProductButton instanceof HTMLButtonElement) {
      viewProductButton.disabled = false;
      viewProductButton.setAttribute('aria-disabled', 'false');
    }

    showStep(0, true);
  };

  section.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const choice = target.closest('[data-cpsbrq-choice]');
    if (choice instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      handleChoice(choice);
      return;
    }

    if (target.closest('[data-cpsbrq-reload]')) {
      event.preventDefault();
      event.stopPropagation();
      resetQuiz();
      return;
    }

    if (target.closest('[data-cpsbrq-view-product]')) {
      event.preventDefault();
      event.stopPropagation();

      if (!currentRecommendation?.quickPanelId && !currentRecommendation?.productHandle) return;

      document.dispatchEvent(
        new CustomEvent('shop-by-room:open-quick-view', {
          detail: {
            panelId: currentRecommendation.quickPanelId,
            productHandle: currentRecommendation.productHandle,
            finish: currentRecommendation.filterFinish,
            size: currentRecommendation.filterSize,
            title: currentRecommendation.title,
          },
        })
      );
    }
  });

  reloadButton?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      resetQuiz();
    }
  });

  showStep(0, false);
}

function init(root = document) {
  if (root instanceof HTMLElement && root.matches(sectionSelector)) {
    initSection(root);
  }

  root.querySelectorAll(sectionSelector).forEach(initSection);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});
